import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import { toastify } from '../../../utils/toast';
import { makeRequest2 } from '../../../utils/makeRequest';
import { concentratorBaseUrl } from '../../../utils/urls';
import Layout from '../component/layout';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';

function Concentrators() {
  const navigate = useNavigate();
  const [concentrators, setConcentrators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredConcentrators, setFilteredConcentrators] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedConcentrator, setSelectedConcentrator] = useState(null);

  // ✅ Proper form state
  const [formData, setFormData] = useState({
    serialNumber: '',
    manufacturer: '',
    range: 0,
    installationStatus: 'inStock',
    ipAddress: '',
  });

  // Fetch concentrators from backend
  const fetchConcentrators = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(concentratorBaseUrl, 'GET');
      if (response.success) {
        const concentratorsData = Array.isArray(response.data.concentrators)
          ? response.data.concentrators
          : [];
        setConcentrators(concentratorsData);
        setFilteredConcentrators(concentratorsData);
      } else {
        toastify(response.error || 'Failed to fetch concentrators', 'error');
      }
    } catch (error) {
      toastify('An error occurred while fetching concentrators', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConcentrators();
  }, []);

  // Filter concentrators
  useEffect(() => {
    const filtered = concentrators.filter((concentrator) => {
      const manufacturerName = concentrator.ManufacturerInfo?.name || '';
      return (
        concentrator.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        manufacturerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        concentrator.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    setFilteredConcentrators(filtered);
  }, [searchTerm, concentrators]);

  // Status badge
  const statusTemplate = (rowData) => {
    const statusColors = {
      online: 'success',
      offline: 'danger',
      Maintenance: 'warning',
    };
    return (
      <span className={`badge bg-${statusColors[rowData.status] || 'secondary'}`}>
        {rowData.status}
      </span>
    );
  };

  // Open modal and pre-fill form
  const handleModalOpen = (concentrator) => {
    if (concentrator) {
      setSelectedConcentrator(concentrator);
      let installationStatus = 'inStock';
      if (concentrator.isFaulty) {
        installationStatus = 'faulty';
      } else if (concentrator.isInstalled) {
        installationStatus = 'installed';
      }
      setFormData({
        serialNumber: concentrator.serialNumber || '',
        manufacturer: concentrator.ManufacturerInfo?.name || '',
        range: concentrator.range || 0,
        installationStatus: installationStatus,
        ipAddress: concentrator.ipAddress || '',
      });
    } else {
      setSelectedConcentrator(null);
      setFormData({
        serialNumber: '',
        manufacturer: '',
        range: 0,
        installationStatus: 'inStock',
        ipAddress: '',
      });
    }
    setShowModal(true);
  };

  // Submit updates
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedConcentrator) {
        const updateData = {
          serialNumber: formData.serialNumber,
          manufacturer: formData.manufacturer,
          range: formData.range,
          isInstalled: formData.installationStatus === 'installed',
          isFaulty: formData.installationStatus === 'faulty',
          inStock: formData.installationStatus === 'inStock',
          ipAddress: formData.ipAddress || null,
        };

        await makeRequest2(
          `${concentratorBaseUrl}/${selectedConcentrator._id}`,
          'PUT',
          updateData
        );
        toastify('Concentrator updated successfully', 'success');
      }
      fetchConcentrators();
      setShowModal(false);
    } catch (error) {
      toastify('An error occurred while saving the concentrator', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Toggle online/offline
  const handleStatusToggle = async (concentrator) => {
    const newStatus = concentrator.status === 'offline' ? 'online' : 'offline';
    try {
      await makeRequest2(
        `${concentratorBaseUrl}/status/${concentrator._id}`,
        'PUT',
        { status: newStatus }
      );
      toastify('Status updated successfully', 'success');
      fetchConcentrators();
    } catch (error) {
      toastify('An error occurred while updating status', 'error');
    }
  };

  // Actions column
  const actionsTemplate = (rowData) => (
    <div className="d-flex align-items-center">
      <Button
        icon="ti ti-edit"
        className="p-button-rounded p-button-info mr-2"
        onClick={() => handleModalOpen(rowData)}
        tooltip="Edit"
      />
      <div className="d-inline-block">
        <div className="mt-1 d-flex align-items-center">
          <i
            className={`ti ${
              rowData.status === 'offline'
                ? 'ti-toggle-left text-danger'
                : 'ti-toggle-right text-primary'
            }`}
            style={{ fontSize: '34px', cursor: 'pointer' }}
            onClick={() => handleStatusToggle(rowData)}
          />
          <span className="ms-2">{rowData.status}</span>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <ConfirmDialog />
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Concentrator Management</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Concentrators</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <div className="row">
                <div className="col-3">
                  <div className="form-search">
                    <i className="ti ti-search"></i>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search Here"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-12">
                  <div style={{ float: 'right' }}>
                    <button
                      className="btn btn-primary"
                      onClick={() =>
                        navigate('/core/water_meter_management/add_new_concentrator')
                      }
                    >
                      Add New Concentrator
                    </button>
                  </div>
                </div>
              </div>
              <div className="dt-responsive table-responsive mt-3">
                <DataTable
                  value={filteredConcentrators}
                  emptyMessage="No concentrators found."
                  sortMode="multiple"
                  paginator
                  rows={10}
                  stripedRows
                  tableStyle={{ minWidth: '50rem' }}
                  loading={loading}
                >
                  <Column field="serialNumber" sortable header="Serial Number" />
                  <Column
                    header="Manufacturer"
                    sortable
                    body={(rowData) =>
                      rowData.ManufacturerInfo ? rowData.ManufacturerInfo.name : 'N/A'
                    }
                  />
                  <Column field="ipAddress" sortable header="IP Address" />
                  <Column field="range" sortable header="Range" />
                  <Column field="status" sortable header="Status" body={statusTemplate} />
                  <Column
                    field="isInstalled"
                    sortable
                    header="Installation Status"
                    body={(rowData) =>
                      rowData.isInstalled ? 'Installed' : 'Not Installed'
                    }
                  />
                  <Column
                    field="inStock"
                    sortable
                    header="Stock Status"
                    body={(rowData) =>
                      rowData.inStock ? 'In Stock' : 'Out of Stock'
                    }
                  />
                  <Column header="Actions" body={actionsTemplate} />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && (
        <div className="modal show d-block">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Concentrator</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      Serial Number<span style={{ color: 'red' }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={formData.serialNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, serialNumber: e.target.value })
                      }
                      placeholder="Enter serial number"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Manufacturer<span style={{ color: 'red' }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={formData.manufacturer}
                      onChange={(e) =>
                        setFormData({ ...formData, manufacturer: e.target.value })
                      }
                      placeholder="Enter manufacturer"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Range<span style={{ color: 'red' }}>*</span>
                    </label>
                    <InputNumber
                      className="form-control"
                      value={formData.range}
                      onValueChange={(e) =>
                        setFormData({ ...formData, range: e.value })
                      }
                      placeholder="Enter range"
                      required
                      min={0}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">IP Address</label>
                    <InputText
                      className="form-control"
                      value={formData.ipAddress}
                      onChange={(e) =>
                        setFormData({ ...formData, ipAddress: e.target.value })
                      }
                      placeholder="Enter IP address"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Installation Status</label>
                    <div>
                      <label>
                        <input
                          type="radio"
                          name="installationStatus"
                          value="installed"
                          checked={formData.installationStatus === 'installed'}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              installationStatus: e.target.value,
                            })
                          }
                        />{' '}
                        Installed
                      </label>
                      &nbsp;&nbsp;
                      <label>
                        <input
                          type="radio"
                          name="installationStatus"
                          value="faulty"
                          checked={formData.installationStatus === 'faulty'}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              installationStatus: e.target.value,
                            })
                          }
                        />{' '}
                        Faulty
                      </label>
                      &nbsp;&nbsp;
                      <label>
                        <input
                          type="radio"
                          name="installationStatus"
                          value="inStock"
                          checked={formData.installationStatus === 'inStock'}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              installationStatus: e.target.value,
                            })
                          }
                        />{' '}
                        In Stock
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Concentrators;
